import { ELEMENT_TYPE } from '@vindr/services/apis/element.api';
import { ResponseGetReports } from '@vindr/services/apis/report.api';
import { Section } from '@vindr/services/apis/templateStructuredReport';
import useRefreshComponent from '@vindr/utils/hooks/useRefreshComponent';
import useWindowSize from '@vindr/utils/hooks/useWindowSize';
import { Empty as AntEmpty, Button, Col, Row } from 'antd';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Empty from '../../ui/Empty';
import InputPanel from './InputPanel';
import OutputPanel from './OutputPanel';
import StructuredElement from './components/elements/structured';
import { StructuredReportElement } from './components/elements/types';
import cx from '@vindr/utils/helpers/cx';

interface StructuredReportForm {
  disabled: boolean;
  onChooseTemplate?: () => void;
  sections: ResponseGetReports['data'][0]['structure']['data'];
  className?: string;
}

export interface StructuredReportHandle {
  getJsonSections: () => StructuredElement[];
  getSections: () => StructuredReportElement[];
}

const StructuredReportForm = forwardRef<StructuredReportHandle, StructuredReportForm>(
  (
    {
      sections,
      disabled,
      // onChange,
      onChooseTemplate,
      className,
    },
    ref
  ) => {
    const { t } = useTranslation('Vindoc');
    const [triggerRerender, setTriggerRerender] = useState<{
      element: StructuredReportElement;
      elementIds?: string[];
      sectionId?: string;
      value?: string;
      preventHighlight?: boolean;
    }>({
      elementIds: [],
      sectionId: null,
      value: '',
      element: null,
    });
    const [sectionData, setSectionData] = useState<StructuredElement[]>([]);
    const [refresh, setRefresh] = useRefreshComponent();
    // const [form] = Form.useForm();
    const prevRef = useRef();

    useEffect(() => {
      // setSectionData(createStructuredElement(sections));
      if (
        JSON.stringify(sections) !== JSON.stringify(prevRef.current) ||
        sections.some((it, index) => it.id !== sectionData[index]?.id)
      ) {
        const newSections = createStructuredElement(sections);
        setSectionData([...newSections]);
      }

      prevRef.current = sections;
    }, [sections]);

    const isShowReportOnly = useMemo(
      () => disabled && sectionData?.length,
      [disabled, sectionData?.length]
    );

    const { width } = useWindowSize();

    const handleRootUpdate = useCallback(
      (value, element, preventHighlight) => {
        // Need to update element to rerender
        if (sectionData?.length) setSectionData(sectionData);
        setTriggerRerender({ element, preventHighlight });
        setRefresh();
      },
      [setRefresh]
    );

    useImperativeHandle(ref, () => {
      return {
        getJsonSections: () => sectionData.map((section) => section.toJSON()) as any,
        getSections: () => [...sectionData] as any,
      };
    });

    return (
      <div
        style={{ height: 'calc(100dvh - 355px)' }}
        className={cx(
          'mb-3 flex min-h-[300px] w-full flex-col space-y-3 overflow-x-visible',
          className
        )}
      >
        <div className="main-container h-full border border-secondary-main">
          <Row style={{ height: '100%', overflow: 'auto' }}>
            <Col
              className="w-full overflow-auto min-[1300px]:h-full"
              sm={24}
              xxl={12}
              style={{
                paddingTop: 0,
                paddingBottom: 0,
                display: isShowReportOnly ? 'none' : 'initial',
              }}
            >
              {sectionData?.length ? (
                <section
                  // layout="vertical"
                  className="[&_.ant-form-item-row]:flex-row [&_.ant-form-item-row]:items-center [&_.ant-form-item-row]:justify-between [&_.ant-form-item-row]:gap-4 [&_.ant-form-item-control]:max-w-[200px] [&_.ant-form-item-control]:flex-[1_1_0]"
                >
                  <InputPanel
                    disabled={disabled}
                    sections={sectionData}
                    onChangeValue={handleRootUpdate}
                  />
                </section>
              ) : (
                <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center">
                  <Empty
                    className="h-1/2 m-0 flex flex-col items-center justify-center text-lg"
                    image={AntEmpty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div>
                        <div>{t('No Data')}</div>
                        <div>{t('Please select template to start editing report.')}</div>
                      </div>
                    }
                  />
                  <Button type="primary" onClick={onChooseTemplate}>
                    {t('Choose Template')}
                  </Button>
                </div>
              )}
            </Col>
            <Col
              className="h-full min-h-[300px] w-full overflow-auto"
              sm={24}
              style={{ paddingTop: 0, paddingBottom: 0 }}
              xxl={isShowReportOnly ? 24 : 12}
            >
              <OutputPanel
                refresh={refresh}
                sections={sectionData}
                triggerRerender={triggerRerender}
              />
            </Col>
          </Row>
        </div>
      </div>
    );
  }
);

StructuredReportForm.displayName = 'StructuredReportForm';

export default StructuredReportForm;

export function createStructuredElement(sections: StructuredReportForm['sections']) {
  return (sections || []).map(createElement);
}

const createElement = (it: Section) => {
  return new StructuredElement({
    ...it,
    value: it.content || it.value,
    elements: it.elements,
    id: it.id,
    name: it.name,
    parent: null,
    level: 0,
    type: ELEMENT_TYPE.STRUCTURE,
  });
};
